/**
 * ============================================================================
 * Saldo de un target de CapEx (componente de lote / cuota de activo) derivado ÚNICAMENTE del
 * subledger append-only `pagos_capex` (migración 20260908, Fase 0/1). Espejo del patrón `resolverSaldo`
 * (proveedor) para el eje CapEx.
 *
 * UNIDAD = MONEDA DEL TARGET, EXACTA, SIN banda cross-currency (decisión Fase 1). El objetivo es nativo
 * en su moneda (ej. 4890 USD) y cada evento congela `monto_target` = el abono convertido a esa misma
 * moneda al TC de la fecha del pago. Así `saldo = objetivo − Σ monto_target` cierra al redondeo (tol 1
 * unidad), sin el drift que obligaba a tolerar 5% cuando se acumulaba en local. `monto_local` NO se usa
 * acá: es solo para sumar entre targets de distinta moneda en totales (10.185).
 *
 * FASE 1: nadie lee esto en runtime todavía (la lectura sigue por el flag `estado`; el cutover es Fase
 * 2). Existe para la verificación del backfill/dual-write y para consumo en Fase 2.
 * ============================================================================
 */

// Tolerancia de redondeo, en la unidad de la moneda del target. Misma-moneda por construcción
// (monto_target ya viene en la moneda del objetivo) → tolerancia FIJA, sin % cross-currency.
export const TOLERANCIA_CAPEX = 1;

/**
 * Núcleo PURO del saldo (única fórmula, 10.1): la comparten getSaldoCapexTarget (single) y los
 * consumidores del batch getSaldosCapexPorTarget (Fase 2).
 * @param {number} objetivo         monto objetivo del target, en su propia moneda.
 * @param {number} aplicadoTarget   Σ monto_target de los eventos vigentes (misma moneda que objetivo).
 * @param {{tolerancia?:number}} opts
 */
export const computarSaldoCapex = (objetivo, aplicadoTarget, { tolerancia = TOLERANCIA_CAPEX } = {}) => {
  const obj = Number(objetivo || 0);
  const aplicado = Number(aplicadoTarget || 0);
  const saldo = obj - aplicado;
  return {
    saldada: saldo <= tolerancia,
    saldo,
    aplicado,
    // hay abonos pero no cierra: parcial (Fase 3). En Fase 1 (todo-o-nada) nunca ocurre.
    parcial: aplicado > 0 && saldo > tolerancia,
  };
};

/**
 * Saldo de UN target a partir de sus eventos crudos de `pagos_capex`.
 * @param {{ objetivo:number }} target  objetivo en la moneda del target.
 * @param {Array} eventos  filas de pagos_capex del target (monto_target, reversado_at).
 * @param {{ tolerancia?:number }} opts
 */
export const getSaldoCapexTarget = (target, eventos = [], opts = {}) => {
  if (!target || target.objetivo == null) return { saldada: false, saldo: 0, aplicado: 0, parcial: false };
  const aplicado = (eventos || [])
    .filter((e) => e && e.reversado_at == null)
    .reduce((s, e) => s + Number(e.monto_target || 0), 0);
  return { ...computarSaldoCapex(target.objetivo, aplicado, opts), nEventos: (eventos || []).length };
};
